import React, {useEffect, useState, useMemo} from "react";
import {StyledEngineProvider, Theme, ThemeProvider} from "@mui/material/styles"
import {
    Environment, GoogleAnalyticsConfiguration,
    ImageMap,
    ItemSearchConfiguration,
    OrgCustomizedConfiguration,
    QuestionsConfigurations
} from '../orgs/OrgCustomizedConfiguration'
import {CssBaseline} from "@mui/material";
import {useTranslation} from 'react-i18next'
import {configurations, defaultConfiguration, initialLanguage} from "./ConfigurationProvider";
import http from "../../http";
import i18n from "i18next";

declare module '@mui/styles/defaultTheme' {
    // eslint-disable-next-line @typescript-eslint/no-empty-interface
    interface DefaultTheme extends Theme {
    }
}

const _host = window.location.host.split(':')[0].toLowerCase()

const _updateBrowserIconAndTitle = (businessName: string, image: string) => {
    document.title = `${businessName} Complaints`

    Array.from(document.querySelectorAll("link[rel~='icon']")).forEach(el => el.remove())

    if (!document.querySelector("link[rel~='icon']")) {
        const icon = document.createElement('link')
        icon.rel = 'icon'

        const apple = document.createElement('link')
        apple.rel = 'apple-touch-icon'

        icon.href = image
        apple.href = image

        document.head.appendChild(icon)
        document.head.appendChild(apple)
    }
}

const _updateBusinessConfiguration = (c: OrgCustomizedConfiguration) => {
    _updateBrowserIconAndTitle(c.OrgName, c.IconImage)
}

_updateBusinessConfiguration(defaultConfiguration)

type OrgCustomizationContextState = {
    orgIdentifier: string
    orgName: string
    t: ((key: string, interpolation?: Record<string, string> | undefined) => string)
    tns: (key: string) => string
    tGetParameter: (tkey: string, key: string) => string | undefined | null
    theme: Theme
    imageMap: ImageMap
    storeUrl: string
    questionsConfiguration: QuestionsConfigurations
    itemSearchConfiguration: ItemSearchConfiguration,
    isDevelopmentEnvironment: boolean,
    environment: Environment,
    googleAnalytics?: GoogleAnalyticsConfiguration,
    supportedLanguages: Set<string>,
    language: string,
    changeLanguage: React.Dispatch<string>
    guestCountryWhiteList: string[]
    countryLanguageMap: { [countryCode: string]: string }
    loadingOrgConfiguration: boolean
    ThirdPartyError: React.ComponentType
}

export const OrgCustomizationContext = React.createContext<OrgCustomizationContextState>({} as OrgCustomizationContextState)

export const OrgCustomizationProvider: React.FC = ({children}) => {
    const [configuration, setConfiguration] = useState(defaultConfiguration)
    const [t] = useTranslation()
    const [language, setLanguage] = useState(initialLanguage)
    const [loadingOrgConfiguration, setLoadingOrgConfiguration] = useState(true)
    const countryLanguageMap = useMemo(
        () =>
            Object.fromEntries(
                configuration.GuestCountryWhitelist.map((item) => [item.countryCode, item.languageCode])
            ),
        [configuration.GuestCountryWhitelist]
    )

    const countryWhitelist = useMemo(
        () => configuration.GuestCountryWhitelist.map((item) => item.countryCode),
        [configuration.GuestCountryWhitelist]
    )
    
    useEffect(() => {
        if (window.location.host.split(':')[0].toLowerCase() === 'localhost') {
            http.get<{ businessOrganization: string }>('/api/Development/BusinessOrganization')
                .then((r) => {
                    const c = configurations[r.data.businessOrganization]
                    setConfiguration(c)
                    _updateBusinessConfiguration(c)
                    setLoadingOrgConfiguration(false)
                })
        } else {
            setLoadingOrgConfiguration(false)
        }
    }, []);

    const tns = (key: string) => `${configuration.OrgIdentifier}:${key}`
    const nt = (key: string, interpolation?: Record<string, string>) => t(tns(key), interpolation)

    return <StyledEngineProvider injectFirst>
        <ThemeProvider theme={configuration.MuiTheme}>
            <CssBaseline/>
            <OrgCustomizationContext.Provider children={!loadingOrgConfiguration ? children : null} value={{
                orgIdentifier: configuration.OrgIdentifier,
                orgName: configuration.OrgName,
                storeUrl: configuration.HostsConfiguration[_host].StoreUrl,
                isDevelopmentEnvironment: configuration.HostsConfiguration[_host].IsDevelopmentEnvironment,
                environment: configuration.HostsConfiguration[_host].Environment,
                googleAnalytics: configuration.HostsConfiguration[_host].GoogleAnalytics,
                tns: tns,
                t: nt,
                tGetParameter: (tkey, key) => {
                    const result = nt(tkey).match(new RegExp(`<${key}>(.*)</${key}>`))

                    return result ? result[1] : undefined
                },
                theme: configuration.MuiTheme,
                imageMap: configuration.ImageMap,
                questionsConfiguration: configuration.QuestionConfigurations,
                itemSearchConfiguration: configuration.ItemSearchConfiguration,
                supportedLanguages: configuration.SupportedLanguages,
                language,
                changeLanguage: (l) => {
                    if (!configuration.SupportedLanguages.has(l.toLowerCase())) return
                    i18n.changeLanguage(l.toLowerCase()).then(() => setLanguage(i18n.language))
                },
                guestCountryWhiteList: countryWhitelist,
                countryLanguageMap,
                loadingOrgConfiguration,
                ThirdPartyError: configuration.ThirdPartyErrorComponent
            }}/>
        </ThemeProvider>
    </StyledEngineProvider>
}
